import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar
} from '@mui/material';
import {
  Person,
  People,
  Business,
  LocationOn
} from '@mui/icons-material';

const PropertyInsightsTable = ({ propertyData, loading, error }) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
        <Typography variant="body2" ml={2}>
          Loading property insights...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!propertyData) {
    return (
      <Box p={3}>
        <Typography variant="body2" color="text.secondary">
          Click on a location and use the insights button to get property data.
        </Typography>
      </Box>
    );
  }

  // Extract homeowner information from API structure
  const getHomeownerInfo = () => {
    const ownership = propertyData.ownership;
    
    if (!ownership) return null;

    const currentOwners = ownership.currentOwners;
    const mailingInfo = ownership.currentOwnerMailingInfo;
    
    if (!currentOwners) return null;

    return {
      ownerNames: currentOwners.ownerNames || [],
      occupancyCode: currentOwners.occupancyCode,
      mailingAddress: mailingInfo?.mailingAddress
    };
  };

  const homeownerInfo = getHomeownerInfo();

  // Determine if property is owner-occupied or tenant-occupied
  const getOccupancyStatus = (code) => {
    switch (code) {
      case 'O': return 'Owner-Occupied';
      case 'T': return 'Tenant-Occupied';
      case 'V': return 'Vacant';
      default: return 'Unknown';
    }
  };

  // Check if mailing address matches property address (indicates owner-occupancy)
  const isOwnerOccupied = () => {
    if (!homeownerInfo?.mailingAddress || !propertyData?.propertyAddress) return null;
    
    const mailingAddress = homeownerInfo.mailingAddress.streetAddress;
    const propertyAddress = propertyData.propertyAddress.streetAddress;
    
    return mailingAddress === propertyAddress;
  };

  const occupancyStatus = getOccupancyStatus(homeownerInfo?.occupancyCode);
  const ownerOccupied = isOwnerOccupied();

  return (
      <Box sx={{overflow:'scroll'}} p={1}>
        {propertyData.mock && (
          <Box sx={{ mb: 1 }}>
          <Chip 
            label="Mock Data" 
            size="small" 
            color="warning" 
          />
          </Box>
        )}

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableBody>
            {/* Property Address */}
            <TableRow>
              <TableCell>
                    <Box display="flex" alignItems="center">
                      <LocationOn sx={{ mr: 1, fontSize: 16 }} />
                  Property Address
                    </Box>
                  </TableCell>
              <TableCell>
                {propertyData.propertyAddress?.streetAddress || propertyData.streetAddress || 'N/A'}
              </TableCell>
            </TableRow>
            
            {/* Homeowner Information - Most Important for Sales */}
            {homeownerInfo && (
              <>
                <TableRow>
                  <TableCell>Current Homeowners</TableCell>
                  <TableCell>
                    {homeownerInfo.ownerNames.length > 0 ? (
                      <>
                        <List dense sx={{ p: 0 }}>
                          {homeownerInfo.ownerNames
                            .filter(owner => owner.fullName) // Only show owners with names
                            .map((owner, index) => (
                              <ListItem key={index} sx={{ p: 0, minHeight: 'auto' }}>
                                <ListItemAvatar sx={{ minWidth: 32 }}>
                                  <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                                    {owner.isCorporate ? <Business /> : <Person />}
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={owner.fullName}
                                  secondary={owner.isCorporate ? 'Corporate Owner' : 'Individual Owner'}
                                  primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                                  secondaryTypographyProps={{ variant: 'caption' }}
                                />
                              </ListItem>
                            ))}
                        </List>
                        {homeownerInfo.ownerNames.filter(owner => owner.fullName).length > 2 && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            ⚠️ {homeownerInfo.ownerNames.filter(owner => owner.fullName).length} owners found. 
                            Add Prospect form will show first 2 owners only.
                          </Typography>
                        )}

                      </>
                    ) : 'No homeowner information available'}
                  </TableCell>
                </TableRow>
                
                <TableRow>
                  <TableCell>Occupancy Status</TableCell>
                  <TableCell>
                    <Chip 
                      label={occupancyStatus} 
                      size="small"
                      color={
                        occupancyStatus === 'Owner-Occupied' ? 'success' : 
                        occupancyStatus === 'Tenant-Occupied' ? 'warning' : 
                        'default'
                      }
                    />
                    {ownerOccupied !== null && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                        {ownerOccupied ? 
                          '✓ Mailing address matches property address' : 
                          '⚠ Mailing address differs from property address'
                        }
                        </Typography>
                    )}
                
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PropertyInsightsTable; 